/**
 * Small controls the render windows share: a slider with its value, and the
 * dotted-path helpers over the photoreal settings.
 */

import React from 'react';
import { PhotoRenderSettings, RANGES, RangeKey } from '../../ui/room3d/photoRender';

export const fmt = (v: number, step: number) => (step >= 1 ? String(Math.round(v)) : v.toFixed(step >= 0.1 ? 1 : 2));

/** A value at a dotted path of the settings. */
export function at(s: PhotoRenderSettings, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), s);
}

/** The settings with one value at a dotted path changed (a fresh object along that path). */
export function withValue(s: PhotoRenderSettings, path: string, value: unknown): PhotoRenderSettings {
  const keys = path.split('.');
  const copy = (o: unknown, i: number): unknown => {
    const node = { ...(o as Record<string, unknown>) };
    node[keys[i]] = i === keys.length - 1 ? value : copy(node[keys[i]], i + 1);
    return node;
  };
  return copy(s, 0) as PhotoRenderSettings;
}

/** A slider over one of RANGES with its value shown; `unit` after the number. */
export const MiniSlider: React.FC<{ label: string; value: number; range: RangeKey; step: number; unit?: string; hint?: string; onChange: (v: number) => void; onReset?: () => void }> = ({ label, value, range, step, unit, hint, onChange, onReset }) => {
  const [lo, hi] = RANGES[range];
  return (
    <div className="rc-row">
      <div className="rc-row-head">
        <span className="rc-label" title={hint}>{label}</span>
        {onReset && <button type="button" className="rc-reset" title="Back to the factory value" aria-label={`Reset ${label}`} onClick={onReset}>↺</button>}
        <span className="rc-value">{fmt(value, step)}{unit ?? ''}</span>
      </div>
      <input className="rc-range" type="range" min={lo} max={hi} step={step} value={value} aria-label={label} onChange={(e) => onChange(parseFloat(e.target.value))} />
    </div>
  );
};
